import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { AnalysisHeaderData, AnalysisPageTabValue } from '@/features/analysisPage/types';
import { mapAnalysisToAnalysisHeader } from '@/shared/api/mappers/analysis';
import i18n from '@/shared/i18n';
import { useDeleteAnalysis, useRenameAnalysis } from '@/shared/hooks/analysis/mutations';
import { useGetAnalysisByIdWithCache } from '@/shared/hooks/analysis/useGetAnalysisByIdWithCache';

export const useAnalysisPageData = (analysisId: string) => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();

  // Déterminer l'onglet actif basé sur les paramètres d'URL
  const activeTab = useMemo((): AnalysisPageTabValue => {
    if (tab === 'niches') return 'niches';
    if (tab === 'history') return 'history';
    if (tab === 'sellers') return 'sellers';
    return 'statistics'; // Défaut
  }, [tab]);

  // Récupérer les données de l'analyse avec statistiques.
  // Création asynchrone (2026-08-05) : tant que le calcul serveur tourne
  // (queued/running), on sonde toutes les 2,5 s — le sondage s'arrête
  // strictement sur ready/failed (états terminaux, plus rien à observer).
  //
  // ⚠️ L'état INCONNU (`undefined` — première requête pas encore aboutie, ou en échec) doit
  // continuer à sonder, JAMAIS s'arrêter (correctif 2026-09-16). L'ancienne forme
  // (`queued || running ? 2500 : false`) traitait l'inconnu comme un état terminal : une seule
  // requête d'en-tête perdue (réseau, bordure, auth) et le sondage était désarmé pour toujours —
  // `computeReady` restait faux, la barre simulée restait à son plafond de 93 %, et seul un
  // rafraîchissement manuel de la page s'en sortait. `query.state.data` est `undefined` tant
  // qu'AUCUNE requête n'a réussi (un `placeholderData` issu du cache des listes ne le remplit
  // pas), donc ce cas est atteignable dès que la première requête échoue.
  const {
    data: analysisData,
    isLoading: isLoadingAnalysis,
    error: analysisError,
    refetch: refetchAnalysis,
  } = useGetAnalysisByIdWithCache(
    {
      id: analysisId,
    },
    {
      refetchInterval: (query: { state: { data?: { getAnalysisById?: { computeState?: string } | null } } }) => {
        const analysis = query.state.data?.getAnalysisById;
        // `null` explicite = l'analyse n'existe pas (supprimée) : état terminal, on arrête.
        if (analysis === null) return false;
        const computeState = analysis?.computeState;
        // Inconnu (requête jamais aboutie) OU calcul en cours : on continue de sonder.
        if (computeState === undefined || computeState === 'queued' || computeState === 'running') {
          return 2500;
        }
        return false; // ready/failed : états terminaux.
      },
    },
  );

  // Hooks centralisés pour les mutations
  const { renameAnalysis } = useRenameAnalysis();
  const { deleteAnalysis } = useDeleteAnalysis();

  // Récupérer les données de l'analyse depuis la query
  const analysisStatsData = analysisData?.getAnalysisById;

  // Mapper les données pour l'en-tête
  const analysisHeaderData = useMemo((): AnalysisHeaderData | null => {
    if (!analysisStatsData) return null;
    return mapAnalysisToAnalysisHeader(analysisStatsData);
  }, [analysisStatsData]);

  // Statistiques de l'analyse
  const analysisStats = useMemo(() => {
    if (!analysisStatsData) return null;
    return {
      analysedProductCount: analysisStatsData.analysedProductCount || 0,
    };
  }, [analysisStatsData]);

  const handleRename = async (newName: string) => {
    if (!analysisId || !newName.trim()) return;
    await renameAnalysis(analysisId, newName);
  };

  const handleDelete = async () => {
    if (!analysisId) return;

    await deleteAnalysis(analysisId);

    // Naviguer vers la page de liste appropriée selon le type
    if (analysisStatsData?.type === 'catalog') {
      navigate('/analysis/products');
    } else if (analysisStatsData?.type === 'profile') {
      navigate('/analysis/vendors');
    } else {
      navigate('/'); // Fallback
    }
  };

  return {
    // État de l'interface
    activeTab,

    // Données de l'analyse
    analysisHeaderData,
    analysisStats,

    // États de chargement et erreurs
    isLoading: isLoadingAnalysis,
    error: analysisError ? i18n.t('analysisDetail.loadError') : null,

    // Actions
    handleRename,
    handleDelete,
    refetch: refetchAnalysis,

    // États des mutations
    isUpdating: false, // Les états de chargement sont gérés dans les hooks centralisés
  };
};
